import tokenManager from '../taskpane/utils/tokenManager';
import MsalAuth from './msalAuth';

/* global Office */
// Office/Outlook Add-In specific code
const onReady = new Promise((resolve, reject) => Office.onReady(resolve));
const msalAuth = new MsalAuth();
const getHost = () => Office.context.mailbox.diagnostics.hostName;
const getItemRestId = (id) => {
  const host = getHost();
  // according to https://docs.microsoft.com/en-us/outlook/add-ins/use-rest-api on Outlook Mobile the itemId is already REST-formatted
  return host === 'OutlookIOS' || host === 'OutlookAndroid' ?
  id :
  Office.context.mailbox.convertToRestId(id, Office.MailboxEnums.RestVersion.v2_0);
};

const officeApi = {
  onReady: (callback) => onReady.then(callback),
  getItemRestId: (id) => getItemRestId(id),
  onMessageLoaded: (callback) => onReady.then(() => {
    const getEmail = () => {
      // on message loaded, cancel all in flight requests with a cancel token.
      tokenManager.cancelAll();
      const item = Office.context.mailbox.item;
      if (!item.itemId || item.itemType === Office.MailboxEnums.ItemType.Appointment) {
      // If there's no itemId, then the email is being composed.
        return callback(item);
      }

      return callback({
        itemId: getItemRestId(item.itemId),
        sender: item.sender || Office.context.mailbox.userProfile,
        from: item.from,
        to: item.to,
        cc: item.cc,
        subject: item.subject,
        body: item.body,
        attachments: (item.attachments || []).map(({ id, name, size, contentType, attachmentType }) => ({
          id: getItemRestId(id),
          name,
          size,
          contentType,
          attachmentType,
        })),
        itemType: item.itemType,
      });
    };

    // Call the handler if the mail item changes
    if (Office.context.requirements.isSetSupported('Mailbox', '1.5')) {
      Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, getEmail, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(`Failed to add event handler: ${result.error.message}`);
        }
      });
    }
    // Or when the add-in is first shown
    getEmail();
  }),
  onAttachmentChanged: (callback) => {
    onReady.then(() => {
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.AttachmentsChanged, callback, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(`Failed to add event handler: ${result.error.message}`);
        }
      });
    });
  },
  onAttendeeChange: (callback) => {
    onReady.then(() => {
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.RecipientsChanged, callback, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(`Failed to add event handler: ${result.error.message}`);
        }
      });
    });
  },
  removeOnAttachmentChanged: () => {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.removeHandlerAsync(Office.EventType.AttachmentsChanged, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(`Failed to remove event handler: ${result.error.message}`);
          reject(new Error(result.error.message));
        }
        resolve(result);
      });
    });
  },
  getMsalConfig: () => {
    return msalAuth.getMsalConfig();
  },
  getUserIdToken: (callback, errorCallback) => {
    onReady.then(() => {
      msalAuth.getTokenUsingNestable().then((result) => {
        if (result?.accessToken?.length) {
          callback(result);
          return;
        }
        msalAuth.getTokenUsingLegacy(callback, errorCallback);
      });
    }).catch((e) => {
      console.log('Error getting user access token: ', e);
      msalAuth.getTokenUsingLegacy(callback, errorCallback);
    });
  },
  getUserInfo: () => {
    return Office.context.mailbox.userProfile;
  },
  getHost: () => getHost(),
  getSubject: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.subject.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getBody: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.body.getAsync(
          'text',
          (result) => {
            resolve(result.value);
          });
    });
  },
  setSubject: (subject) => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.subject.setAsync(subject, (asyncResult) => resolve(asyncResult.value));
    });
  },
  getAttachments: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.getAttachmentsAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getAttachmentContent: (attachmentId) => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, (asyncResult) => resolve(asyncResult));
    });
  },
  // See docs - https://docs.microsoft.com/en-us/javascript/api/outlook/office.messagecompose?view=outlook-js-1.8&preserve-view=true#outlook-office-messagecompose-addfileattachmentasync-member(1)
  // Attaching files via URI does not work in Windows desktop, we therefore attach files using base64 content bytes there
  putAttachments: (uri, name) => {
    return new Promise((resolve) => Office.context.mailbox.item.addFileAttachmentAsync(uri, name, (asyncResult) => resolve(asyncResult)));
  },
  putAttachmentContent: (contentBytes, name) => {
    return new Promise((resolve) => Office.context.mailbox.item.addFileAttachmentFromBase64Async(contentBytes, name, { isInline: false }, (asyncResult) => resolve(asyncResult)));
  },
  // This function can be used for debugging on desktop in compose mode only
  putText: (text) => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.body.prependAsync(text, (asyncResult) => resolve(asyncResult));
    });
  },
  getEWSId: (id) => {
    return Office.context.mailbox.convertToEwsId(id, Office.MailboxEnums.RestVersion.v2_0);
  },
  getPlatform: () => {
    return Office.context.platform;
  },
  saveEmail: () => {
    return new Promise((resolve) => Office.context.mailbox.item.saveAsync((asyncResult) => resolve(asyncResult.value)));
  },
  getCategories: () => {
    return new Promise((resolve) => Office.context.mailbox.item.categories.getAsync((asyncResult) => resolve(asyncResult.value)));
  },
  openUiDisplay: (uri) => {
    return new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(uri,
          { width: 30, height: 60, left: (window.screen.width - 550) / 2, top: (window.screen.height - 650) / 2, promptBeforeOpen: false, displayInIframe: true },
          ({ value, error }) => {
            value ? resolve(value) : reject(error);
          });
    });
  },
  messageParent: (message) => new Promise((resolve, reject) => Office.context.ui.messageParent(message)),
  getItemIdAsync: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.getItemIdAsync((asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          console.error('Failed to get Item Id', asyncResult.error.message);
          resolve();
        } else {
          resolve(asyncResult.value);
        }
      },
      );
    });
  },
  getEventOrganizer: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.organizer.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getEventStart: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.start.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getEventEnd: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.end.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getEventLocation: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.location.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getEventRecurrence: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.recurrence.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getEventSubject: () => {
    return new Promise((resolve) => {
      Office.context.mailbox.item.subject.getAsync((asyncResult) => resolve(asyncResult.value));
    });
  },
  getComposeEventAttendees: () => {
    // TODO: we can probably refactor this...
    const requiredAttendees = new Promise((resolve, reject) => {
      Office.context.mailbox.item.requiredAttendees.getAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const emailAddresses = asyncResult.value.map((attendee) => attendee.emailAddress.toLowerCase());
          resolve(emailAddresses);
        } else {
          reject(new Error('unable to retrieve required attendees'));
        }
      });
    });
    const optionalAttendees = new Promise((resolve, reject) => {
      Office.context.mailbox.item.optionalAttendees.getAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const emailAddresses = asyncResult.value.map((attendee) => attendee.emailAddress.toLowerCase());
          resolve(emailAddresses);
        } else {
          reject(new Error('unable to retrieve optional attendees'));
        }
      });
    });
    const organizer = new Promise((resolve, reject) => {
      Office.context.mailbox.item.organizer.getAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value.emailAddress.toLowerCase());
        } else {
          reject(new Error('unable to retrieve organizer'));
        }
      });
    });

    return [requiredAttendees, optionalAttendees, organizer];
  },
  getEventAttendeesWithNames: () => {
    try {
      const item = Office.context.mailbox.item;
      if (!item.itemId) {
        const requiredAttendees = new Promise((resolve, reject) => {
          Office.context.mailbox.item.requiredAttendees.getAsync((asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              const emailAddresses = asyncResult.value.map((attendee) => ({ ...attendee, emailAddress: attendee.emailAddress.toLowerCase() }));
              resolve(emailAddresses);
            } else {
              reject(new Error('unable to retrieve required attendees'));
            }
          });
        });
        const optionalAttendees = new Promise((resolve, reject) => {
          Office.context.mailbox.item.optionalAttendees.getAsync((asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              const emailAddresses = asyncResult.value.map((attendee) => ({ ...attendee, emailAddress: attendee.emailAddress.toLowerCase() }));
              resolve(emailAddresses);
            } else {
              reject(new Error('unable to retrieve optional attendees'));
            }
          });
        });
        const organizer = new Promise((resolve, reject) => {
          Office.context.mailbox.item.organizer.getAsync((asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              resolve({ ...asyncResult.value, emailAddress: asyncResult.value.emailAddress.toLowerCase() });
            } else {
              reject(new Error('unable to retrieve organizer'));
            }
          });
        });
        return Promise.all([requiredAttendees, optionalAttendees, organizer]);
      } else {
        const requiredAttendees = Office.context.mailbox.item.requiredAttendees.map((attendee) => ({ ...attendee, emailAddress: attendee.emailAddress.toLowerCase() }));
        const optionalAttendees = Office.context.mailbox.item.optionalAttendees.map((attendee) => ({ ...attendee, emailAddress: attendee.emailAddress.toLowerCase() }));
        const organizer = { ...Office.context.mailbox.item.organizer, emailAddress: Office.context.mailbox.item.organizer.emailAddress.toLowerCase() };
        return [requiredAttendees, optionalAttendees, organizer];
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  getEventAttendees: () => {
    const requiredAttendees = Office.context.mailbox.item.requiredAttendees.map((attendee) => attendee.emailAddress.toLowerCase());
    const optionalAttendees = Office.context.mailbox.item.optionalAttendees.map((attendee) => attendee.emailAddress.toLowerCase());
    const organizer = Office.context.mailbox.item.organizer.emailAddress.toLowerCase();

    // this is required to fix the inconsistent behavior between desktop and web
    // desktop does not include the organizer in the required attendees list (ticket: EM-1505)
    if (!requiredAttendees?.includes(organizer)) {
      requiredAttendees.push(organizer);
    }

    return [requiredAttendees, optionalAttendees, organizer];
  },
  saveEvent: () => {
    return new Promise((resolve) => Office.context.mailbox.item.saveAsync((asyncResult) => resolve(asyncResult.value)));
  },
  removeEmailFromEvent: (emailAddress, currentMapping) => {
    let updatedRequired;
    let updatedOptional;
    for (const key of Object.keys(currentMapping)) {
      if (currentMapping[key].includes(emailAddress)) {
        if (key === 'required') {
          updatedRequired = currentMapping[key].filter((email) => email.toLowerCase() !== emailAddress);
        } else if (key === 'optional') {
          updatedOptional = currentMapping[key].filter((email) => email.toLowerCase() !== emailAddress);
        }
      }
    }
    if (updatedRequired) {
      Office.context.mailbox.item.requiredAttendees.setAsync(updatedRequired);
    } else if (updatedOptional) {
      Office.context.mailbox.item.optionalAttendees.setAsync(updatedOptional);
    }
  },
  addEmailsToEvent: (emailAddresses, currentRequired) => {
    Office.context.mailbox.item.requiredAttendees.setAsync([...currentRequired].concat(emailAddresses));
  },
  getSharedProperties: (attendees = []) => {
    if (!Office.context.mailbox.item.getSharedPropertiesAsync) {
    // if we are not on a delegated calendar, return an empty object
      return {};
    }
    return new Promise((resolve) => {
      Office.context.mailbox.item.getSharedPropertiesAsync((asyncResult) => {
        const [requiredAttendees = [], optionalAttendees = [], organizer = {}] = attendees;
        const eventAttendeesForDelegatedCalendar = [...requiredAttendees, ...optionalAttendees];
        if (!eventAttendeesForDelegatedCalendar.find((attendee) => attendee.emailAddress === organizer.emailAddress)) {
          eventAttendeesForDelegatedCalendar.push(organizer);
        }
        asyncResult['value']['eventAttendeesForDelegatedCalendar'] = eventAttendeesForDelegatedCalendar;
        resolve(asyncResult.value);
      });
    });
  },
};

export default officeApi;
